/**
 * Every contest rule setting, as a closed set the compiler checks. This is the
 * direct fix for v1's escaped enums (data-model.md, "What v1 got wrong", item
 * 2): a rule setting is never a loose string read out of the database. Each set
 * below is taken verbatim from contest-api.md's /contest/validate-config
 * request DSL, so the engine's unions and the wire enums cannot drift apart.
 */

/** SUR-R8. How the contest's date range is sliced into rounds. */
export const ROUND_GROUPINGS = ["day", "week"] as const;
export type typeRoundGrouping = (typeof ROUND_GROUPINGS)[number];

/** ISO-8601 weekday numbering: 1 = Monday … 7 = Sunday. SUR-R9. */
export const WEEK_START_DAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export type typeWeekStartDay = (typeof WEEK_START_DAYS)[number];

/** SUR-R9. Tuesday. */
export const DEFAULT_WEEK_START_DAY: typeWeekStartDay = 2;

/** SUR-R13. One moment for the whole round, or staggered per game. */
export const ROUND_LOCK_TIMINGS = ["firstGame", "lastGame"] as const;
export type typeRoundLockTiming = (typeof ROUND_LOCK_TIMINGS)[number];

/** SUR-R16. Pick the winner (moneyline), or Beat the spread. */
export const PICK_BASES = ["moneyline", "spread"] as const;
export type typePickBasis = (typeof PICK_BASES)[number];

/** SUR-R17. Whole-round freeze at 24 hours out, or per-game live updates. */
export const SPREAD_TIMINGS = ["lockedEarly", "live"] as const;
export type typeSpreadTiming = (typeof SPREAD_TIMINGS)[number];

/** SUR-R29, SUR-R30. Win them all, or Win half or more (half passes). */
export const ADVANCE_CRITERIA = ["winAll", "winHalfOrMore"] as const;
export type typeAdvanceCriterion = (typeof ADVANCE_CRITERIA)[number];

/** SUR-R77. How many times one player may be revived in a contest. */
export const REVIVES_ALLOWED_MODES = ["unlimited", "limited"] as const;
export type typeRevivesAllowedMode = (typeof REVIVES_ALLOWED_MODES)[number];

/** SUR-R42, SUR-R43. Count as loss may consume a final life and eliminate. */
export const POSTPONEMENT_POLICIES = [
  "makeUpNextRound",
  "countAsLoss",
] as const;
export type typePostponementPolicy = (typeof POSTPONEMENT_POLICIES)[number];

/**
 * SUR-R40, SUR-R41. Chosen at contest setup with deliberately no platform
 * default. A spread push follows the same rule (SUR-R17).
 */
export const TIE_POLICIES = ["tieIsWin", "tieIsLoss"] as const;
export type typeTiePolicy = (typeof TIE_POLICIES)[number];

/**
 * SUR-R1 as amended and SUR-R93 (owner, 2026-10-08, ISSUE-1095). tbd means the
 * contest runs until one survivor is left or the league season's last
 * scheduled game.
 */
export const END_DATE_MODES = ["date", "tbd"] as const;
export type typeEndDateMode = (typeof END_DATE_MODES)[number];

/**
 * SUR-R63. Exactly three options. The old four — rounds survived, longest pick
 * streak, fewest losses, and award-nobody — are retired and must not reappear.
 */
export const CONTEST_TIEBREAKERS = [
  "splitMedal",
  "noWinner",
  "commissionerPick",
] as const;
export type typeContestTiebreaker = (typeof CONTEST_TIEBREAKERS)[number];

/**
 * The kind of contest a pool runs. Survivor's rules live in the round, pick
 * and grading modules. Squares (2026-11-01, Squares Redesign v3.1.1) and
 * Strip Card are structurally separate mini-domains with no rounds, picks or
 * elimination, so each keeps its own rule module: src/squares/ and
 * src/strips/. Pick'em (ISSUE-1301) shares survivor's rounds, lock, grading
 * and spread rules and keeps only what survivor has no word for (confidence
 * ranks, round points, round winners, the points board) in src/pickem/. This
 * union stays the seam every caller branches on.
 */
export const ENGINE_POOL_TYPES = [
  "survivor",
  "squares",
  "stripCard",
  "pickem",
] as const;
export type typePoolType = (typeof ENGINE_POOL_TYPES)[number];

/**
 * A contest's complete rule set. contest-api freezes a copy of this onto each
 * round when the round opens (SUR-R73), and grading resolves against the
 * round's own frozen copy — never the contest's current settings — so a
 * Commissioner's mid-contest change can never re-grade an already-opened round.
 */
export type typeContestConfig = {
  poolType: typePoolType;
  roundGrouping: typeRoundGrouping;
  weekStartDay: typeWeekStartDay;
  roundLockTiming: typeRoundLockTiming;
  pickBasis: typePickBasis;
  spreadTiming: typeSpreadTiming | null;
  advanceCriterion: typeAdvanceCriterion;
  livesAllowed: number;
  requiredPicksPerRound: number;
  maxEntriesPerMember: number;
  teamReuseAllowed: boolean;
  teamReuseLockoutDays: number | null;
  teamReuseMaxCount: number | null;
  skipsAllowed: boolean;
  skipsMaxPerContest: number | null;
  revivesAllowed: typeRevivesAllowedMode;
  revivesAllowedCount: number | null;
  postponementPolicy: typePostponementPolicy;
  tiePolicy: typeTiePolicy;
  tiebreaker: typeContestTiebreaker;
  winPoints: number;
  lossPoints: number;
};

/** SUR-R73. The copy frozen onto a round when it opens. */
export type typeRoundRuleSnapshot = typeContestConfig;
